import type { TimeFormat } from '@app/api/Branches/types';

import type {
  AppointmentPreviewField,
  CalendarBreak,
  CalendarEvent,
  CalendarSlotMenu,
  Employee,
} from '@pages/Calendar/data';
import { DragPreviewOverlay } from '@pages/Calendar/ui/DragPreviewOverlay';
import type {
  IndividualCalendarDay,
  IndividualCalendarDayAccess,
} from '@pages/IndividualCalendar/data';

import { DayColumn } from '../../DayColumn';
import { useDaysViewport } from '../hooks';

import type { FC } from 'react';

import '../styles/DaysViewport.scss';

export interface DaysViewportProps {
  days: IndividualCalendarDay[];
  dayAccessByDate: Record<string, IndividualCalendarDayAccess>;
  dayColumns: Employee[];
  startTime: string;
  endTime: string;
  stepMinutes: number;
  timeFormat?: TimeFormat | null;
  intervalHeightPx: number;
  onSlotClick?: (date: string, time: string, endTime: string) => void;
  onSlotBreakClick?: (date: string, time: string, endTime: string) => void;
  onBreakClick: (date: string, calendarBreak: CalendarBreak) => void;
  onEventUpdate: (
    eventId: string,
    newStart: string,
    newEnd: string,
    newDate?: string
  ) => void;
  onRecordClick: (event: CalendarEvent) => void;
  canShowContacts: boolean;
  previewFields: AppointmentPreviewField[];
  activeSlotMenu: CalendarSlotMenu | null;
  onSlotMenuOpen: (slotMenu: CalendarSlotMenu) => void;
  onSlotMenuClose: () => void;
}

export const DaysViewport: FC<DaysViewportProps> = (props) => {
  const {
    days,
    dayAccessByDate,
    dayColumns,
    startTime,
    endTime,
    stepMinutes,
    timeFormat,
    intervalHeightPx,
    onSlotClick,
    onSlotBreakClick,
    onBreakClick,
    onEventUpdate,
    onRecordClick,
    canShowContacts,
    previewFields,
    activeSlotMenu,
    onSlotMenuOpen,
    onSlotMenuClose,
  } = props;
  const { dragState, isDragPreviewVisible, dragStateChangeHandler } =
    useDaysViewport({ dayColumns });

  return (
    <div className="DaysViewport" data-testid="days-viewport">
      <div
        className="DaysViewport__track"
        style={{
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
        }}
      >
        {days.map((day) => {
          const dayAccess = dayAccessByDate[day.date];
          const isContentHidden = Boolean(dayAccess?.isContentHidden);

          return (
            <div
              key={day.id}
              className="DaysViewport__column"
              data-day-column-id={day.id}
              data-employee-column-id={isContentHidden ? undefined : day.id}
            >
              <DayColumn
                day={day}
                dayColumns={dayColumns}
                isContentHidden={isContentHidden}
                canCreateRecords={Boolean(dayAccess?.canCreateRecords)}
                canManageRecords={Boolean(dayAccess?.canManageRecords)}
                canOpenRecords={Boolean(dayAccess?.canOpenRecords)}
                startTime={startTime}
                endTime={endTime}
                stepMinutes={stepMinutes}
                timeFormat={timeFormat}
                intervalHeightPx={intervalHeightPx}
                onSlotClick={onSlotClick}
                onSlotBreakClick={onSlotBreakClick}
                onBreakClick={onBreakClick}
                onEventUpdate={onEventUpdate}
                onDragStateChange={dragStateChangeHandler}
                onRecordClick={onRecordClick}
                canShowContacts={canShowContacts}
                previewFields={previewFields}
                activeSlotMenu={activeSlotMenu}
                onSlotMenuOpen={onSlotMenuOpen}
                onSlotMenuClose={onSlotMenuClose}
              />
            </div>
          );
        })}

        {dragState !== null && isDragPreviewVisible && (
          <DragPreviewOverlay
            dragState={dragState}
            employees={dayColumns}
            columnWidth={0}
            hasOverflow={false}
          />
        )}
      </div>
    </div>
  );
};
